import { getAppSDK } from '@finn/ua-app';
import { useCurrentApp, useUniversalRouter } from '@finn/ui-utils';
import isEqual from 'lodash/isEqual';
// TILL MIGRATION IS DONE useRouter will return null;
import { usePathname, useSearchParams } from 'next/navigation';
import {
  ComponentType,
  Context as ReactContext,
  createContext,
  memo,
  ReactNode,
  useContext,
  useMemo,
} from 'react';

import { FilterValuesObject } from '../../core';

export const shallowRouterRaw = ({ app }: { app: string }) => ({
  push: (
    url: string,
    { scroll = true }: { scroll: boolean } = { scroll: true }
  ) => {
    let nextUrl = url;
    const globalObject = typeof window === 'object' ? window : undefined;
    const currentScroll = !scroll ? globalObject?.scrollY || 0 : 0;

    if (url.startsWith('/') && !url?.includes('de-DE') && app !== 'b2b') {
      nextUrl = `/de-DE${url}`;
    }

    if (typeof window !== 'undefined') {
      window.history.pushState(
        currentScroll ? { scrollY: currentScroll } : null,
        '',
        nextUrl
      );

      const appSDK = getAppSDK();

      appSDK?.sendMessageToApp({
        type: 'event:navigate',
        value: nextUrl,
      });
    }
  },
  replace: (
    url: string,
    { scroll = true }: { scroll: boolean } = { scroll: true }
  ) => {
    let nextUrl = url;
    const globalObject = typeof window === 'object' ? window : undefined;
    const currentScroll = !scroll ? globalObject?.scrollY || 0 : 0;
    if (url.startsWith('/') && !url?.includes('de-DE') && app !== 'b2b') {
      nextUrl = `/de-DE${url}`;
    }

    if (typeof window !== 'undefined') {
      window.history.replaceState(
        currentScroll ? { scrollY: currentScroll } : null,
        '',
        nextUrl
      );

      const appSDK = getAppSDK();

      appSDK?.sendMessageToApp({
        type: 'event:navigate',
        value: nextUrl,
      });
    }
  },
});

export const useShallowRouter = () => {
  const app = useCurrentApp();

  return shallowRouterRaw({ app });
};

// TODO: Move this outside `lib` to some place else
export const PRESERVED_QUERY_PARAMS = [
  'reason',
  'reason_type',
  'prompt',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'gclid',
  'gbraid',
  'msclkid',
  'campaign',
  'utm_term',
  'utm_content',
  'awc',
  'page',
];

// TODO: Move this outside `lib` to some place else
function restorePreservedQueryParams(url: URL): void {
  const prevParams = new URLSearchParams(window.location.search);

  for (const [key, value] of prevParams.entries()) {
    const isFeatureFlag = /^(feat|exp)-/g.test(key);
    if (isFeatureFlag || PRESERVED_QUERY_PARAMS.includes(key)) {
      url.searchParams.append(key, value);
    }
  }
}

type FilterMapper<ValuesObject = object> = {
  serialize(input: ValuesObject): string;
  deserialize(input: string): ValuesObject;
};

type FilterContextValue<ValuesObject> = {
  readonly filterValues: ValuesObject;
  readonly filterMapper: FilterMapper<ValuesObject>;
};

type FilterContextType<ValuesObject> = ReactContext<
  FilterContextValue<ValuesObject>
>;

function throwNoProviderError(): never {
  throw new Error(
    'FilterContext value is not provided or accessed outside of FilterContext.Provider'
  );
}

export function createFilterContext<
  ValuesObject,
>(): FilterContextType<ValuesObject> {
  return createContext({
    get filterValues(): ValuesObject {
      return throwNoProviderError();
    },

    get filterMapper(): FilterMapper<ValuesObject> {
      return throwNoProviderError();
    },
  });
}

type FilterProviderProps<ValuesObject> = {
  filterMapper: FilterMapper<ValuesObject>;
  children?: ReactNode;
};

export function createFilterProvider<ValuesObject>(
  Context: FilterContextType<ValuesObject>
): ComponentType<FilterProviderProps<ValuesObject>> {
  return memo(function FilterProvider({ filterMapper, children }) {
    const pathname = usePathname();
    const router = useUniversalRouter();
    const query = useSearchParams();
    const asPath =
      router?.asPath ||
      `${pathname}${query?.toString() ? `${pathname?.includes('?') ? '&' : '?'}${query?.toString()}` : ''}`;

    const filterValues = useMemo(() => {
      return filterMapper.deserialize(asPath);
    }, [filterMapper, asPath]);

    const providerValue = useMemo(() => {
      return {
        filterValues,
        filterMapper,
      };
    }, [filterValues, filterMapper]);

    return (
      <Context.Provider value={providerValue}>{children}</Context.Provider>
    );
  });
}

type FilterConsumerProps<ValuesObject> = {
  children: (value: FilterContextValue<ValuesObject>) => ReactNode;
};

export function createFilterConsumer<ValuesObject>(
  Context: FilterContextType<ValuesObject>
): ComponentType<FilterConsumerProps<ValuesObject>> {
  return memo(function FilterConsumer({ children }) {
    return <Context.Consumer>{children}</Context.Consumer>;
  });
}

type NavigateOptions = {
  replaceURL?: boolean;
};

type FilterValueSelector<ValuesObject, SelectorResult extends unknown> = (
  values: ValuesObject
) => SelectorResult;

type UseFilterValues<ValuesObject> = {
  (): ValuesObject;

  <SelectorResult>(
    selector?: FilterValueSelector<ValuesObject, SelectorResult>
  ): SelectorResult;
};

type UseMergeFilterValues<ValuesObject> = () => (
  nextFilterValues: ValuesObject,
  options?: NavigateOptions
) => void;

type UseReplaceFilterValues<ValuesObject> = () => (
  nextFilterValues: ValuesObject,
  options?: NavigateOptions
) => void;

type CreateFilterHooksResult<ValuesObject> = {
  useFilterValues: UseFilterValues<ValuesObject>;
  useMergeFilterValues: UseMergeFilterValues<ValuesObject>;
  useReplaceFilterValues: UseReplaceFilterValues<ValuesObject>;
};

export function createFilterHooks<ValuesObject>(
  Context: FilterContextType<ValuesObject>
): CreateFilterHooksResult<ValuesObject> {
  return {
    useFilterValues<SelectorResult>(
      selector?: FilterValueSelector<ValuesObject, SelectorResult>
    ) {
      const { filterValues } = useContext(Context);

      if (!selector) {
        return filterValues;
      }

      return selector(filterValues);
    },

    useMergeFilterValues() {
      const { filterValues, filterMapper } = useContext(Context);
      const shallowRouter = useShallowRouter();

      return (nextFilterValues, options) => {
        if (isEqual(filterValues, nextFilterValues)) {
          return;
        }

        const nextURL = filterMapper.serialize({
          ...filterValues,
          ...nextFilterValues,
        });

        if (options?.replaceURL) {
          shallowRouter?.replace(nextURL, { scroll: false });
        } else {
          shallowRouter?.push(nextURL, { scroll: false });
        }
      };
    },

    useReplaceFilterValues() {
      const shallowRouter = useShallowRouter();
      const { filterValues, filterMapper } = useContext(Context);

      return (nextFilterValues, options) => {
        if (isEqual(filterValues, nextFilterValues)) {
          return;
        }

        const nextURL = new URL(
          filterMapper.serialize(nextFilterValues),
          window.location.origin
        );

        restorePreservedQueryParams(nextURL);

        const nextPath = `${nextURL.pathname}${nextURL.search}`;

        if (options?.replaceURL) {
          shallowRouter?.replace(nextPath);
        } else {
          shallowRouter?.push(nextPath);
        }
      };
    },
  };
}

export const FilterContext = createFilterContext<FilterValuesObject>();

export const FilterProvider = createFilterProvider(FilterContext);
export const FilterConsumer = createFilterConsumer(FilterContext);

const { useFilterValues, useMergeFilterValues, useReplaceFilterValues } =
  createFilterHooks<FilterValuesObject>(FilterContext);

export { useFilterValues, useMergeFilterValues, useReplaceFilterValues };
